import React from 'react'
import { getHitKind } from '../../utils/search/getHitKind'
import SearchPreviewQuestion from './preview/SearchPreviewQuestion'
import SearchPreviewSet from './preview/SearchPreviewSet'
import SearchPreviewPack from './preview/SearchPreviewPack'
import SearchPreviewFolder from './preview/SearchPreviewFolder'
import SearchPreviewInfo from './preview/SearchPreviewInfo'

// Routes the currently-highlighted dropdown suggestion to the right preview
// component. The pane sits inside the autosuggest container as a sibling of
// the suggestions list, so it mounts/unmounts with the dropdown itself.
function SearchPreviewPane({ suggestion, query }) {
  // ── Empty highlight (focused, nothing selected yet) ──────────────────────
  if (!suggestion) {
    return (
      <div className='searchPreviewPane searchPreviewPane--empty'>
        <SearchPreviewInfo variant='empty' query={query} />
      </div>
    )
  }

  // ── Special / synthetic rows (info card) ─────────────────────────────────
  if (suggestion.__quickAction) {
    return (
      <div className='searchPreviewPane searchPreviewPane--info'>
        <SearchPreviewInfo variant='quickAction' suggestion={suggestion} query={query} />
      </div>
    )
  }
  if (suggestion.__helpShowMore) {
    return (
      <div className='searchPreviewPane searchPreviewPane--info'>
        <SearchPreviewInfo variant='helpShowMore' suggestion={suggestion} query={query} />
      </div>
    )
  }
  if (suggestion.__external) {
    return (
      <div className='searchPreviewPane searchPreviewPane--info'>
        <SearchPreviewInfo variant='external' suggestion={suggestion} query={query} />
      </div>
    )
  }
  if (suggestion.__nav) {
    return (
      <div className='searchPreviewPane searchPreviewPane--info'>
        <SearchPreviewInfo variant='nav' suggestion={suggestion} query={query} />
      </div>
    )
  }
  if (suggestion.showMore) {
    return (
      <div className='searchPreviewPane searchPreviewPane--info'>
        <SearchPreviewInfo variant='showMore' suggestion={suggestion} query={query} />
      </div>
    )
  }

  // ── Recent items: route by their cached itemType ─────────────────────────
  if (suggestion.__recent) {
    const { item, __recentItemType: kind } = suggestion
    if (!item) {
      return (
        <div className='searchPreviewPane searchPreviewPane--info'>
          <SearchPreviewInfo variant='recentMissing' suggestion={suggestion} query={query} />
        </div>
      )
    }
    if (kind === 'set') {
      const setHit = {
        id: item.id,
        name: item.name,
        questions: item.questions,
        clientModified: item.clientModified,
      }
      return (
        <div className='searchPreviewPane searchPreviewPane--set'>
          <SearchPreviewSet hit={setHit} />
        </div>
      )
    }
    if (kind === 'question') {
      return (
        <div className='searchPreviewPane searchPreviewPane--question'>
          <SearchPreviewQuestion hit={{ id: item.id, ...item }} />
        </div>
      )
    }
    return (
      <div className='searchPreviewPane searchPreviewPane--info'>
        <SearchPreviewInfo variant='recentMissing' suggestion={suggestion} query={query} />
      </div>
    )
  }

  // ── Library entities — discriminate by Algolia document shape ────────────
  const kind = getHitKind(suggestion)
  if (kind === 'question') {
    return (
      <div className='searchPreviewPane searchPreviewPane--question'>
        <SearchPreviewQuestion hit={suggestion} />
      </div>
    )
  }
  if (kind === 'set') {
    return (
      <div className='searchPreviewPane searchPreviewPane--set'>
        <SearchPreviewSet hit={suggestion} />
      </div>
    )
  }
  if (kind === 'repo') {
    return (
      <div className='searchPreviewPane searchPreviewPane--pack'>
        <SearchPreviewPack hit={suggestion} />
      </div>
    )
  }
  if (kind === 'folder') {
    return (
      <div className='searchPreviewPane searchPreviewPane--folder'>
        <SearchPreviewFolder hit={suggestion} />
      </div>
    )
  }

  // Unknown / unhandled — render the empty info card.
  return (
    <div className='searchPreviewPane searchPreviewPane--empty'>
      <SearchPreviewInfo variant='empty' query={query} />
    </div>
  )
}

export default SearchPreviewPane
